import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, PackageSearch, Search } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import demoReconciliationService from '@services/demoReconciliationService';
import {
  DemoPendingCustomer,
  DemoReconciliationHistory,
  demoStatusLabel,
} from '@/types/demoReconciliation';
import { formatCurrencyBRL, formatDateBR, formatDateTimeBR } from '@pages/financial/financialUtils';
import { CopyLink } from './CopyLink';

type TabId = 'pending' | 'history';

const HISTORY_PAGE_SIZE = 20;

/**
 * Conciliação de Demonstração — lista.
 *
 * "Pendentes": clientes com pedidos de demonstração ainda não conciliados
 * (clique abre a área de trabalho da cliente). "Histórico": conciliações já
 * feitas, com a proposta gerada no CRM e os links de pagamento.
 */
export const DemoReconciliationListView: React.FC = () => {
  const [tab, setTab] = useState<TabId>('pending');

  return (
    <PermissionGuard permission="orders.demo_reconciliation" showError>
      <div className="space-y-6">
        <div>
          <h1 className="text-h1 mb-2">Conciliação de Demonstração</h1>
          <p className="text-slate-600 text-sm">
            Confronte os pedidos de demonstração enviados com os recebimentos de materiais devolvidos.
            O que ficou com a cliente vira uma proposta no CRM.
          </p>
        </div>

        <div className="border-b border-slate-200">
          <nav className="flex space-x-8" aria-label="Abas">
            {([
              { id: 'pending', label: 'Pendentes' },
              { id: 'history', label: 'Histórico' },
            ] as { id: TabId; label: string }[]).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === t.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'pending' ? <PendingTab /> : <HistoryTab />}
      </div>
    </PermissionGuard>
  );
};

// ─── Pendentes ──────────────────────────────────────────────────

const PendingTab: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<DemoPendingCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await demoReconciliationService.getPendingCustomers(term));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [term]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const onSearch = (value: string) => {
    setSearch(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setTerm(value), 400);
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              className="input w-full pl-9 text-sm"
              placeholder="Buscar por cliente ou documento…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="m-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-sm gap-2">
            <PackageSearch size={28} className="text-slate-300" />
            Nenhuma cliente com demonstração pendente.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Cliente</th>
                  <th className="text-left font-medium px-4 py-2">Documento</th>
                  <th className="text-right font-medium px-4 py-2">Nº pedidos</th>
                  <th className="text-left font-medium px-4 py-2">Vendedora</th>
                  <th className="text-right font-medium px-4 py-2">Valor enviado</th>
                  <th className="text-left font-medium px-4 py-2">Desde</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr
                    key={r.customer_id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/demo-reconciliation/${r.customer_id}`)}
                  >
                    <td className="px-4 py-2 font-medium text-slate-800">{r.customer_name}</td>
                    <td className="px-4 py-2 text-slate-600">{r.document || '—'}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{r.orders_count}</td>
                    <td className="px-4 py-2 text-slate-700">{r.seller_name || '—'}</td>
                    <td className="px-4 py-2 text-right text-slate-800">{formatCurrencyBRL(r.products_total)}</td>
                    <td className="px-4 py-2 text-slate-700">{formatDateBR(r.oldest_order_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Histórico ──────────────────────────────────────────────────

const HistoryTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<DemoReconciliationHistory[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await demoReconciliationService.getHistory(page, HISTORY_PAGE_SIZE);
        if (cancelled) return;
        setRows(data.items ?? []);
        setTotal(data.total ?? 0);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar histórico');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  return (
    <Card>
      <CardContent className="p-0">
        {error && (
          <div className="m-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">Nenhuma conciliação registrada.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Data</th>
                  <th className="text-left font-medium px-4 py-2">Cliente</th>
                  <th className="text-left font-medium px-4 py-2">Vendedora</th>
                  <th className="text-left font-medium px-4 py-2">Situação</th>
                  <th className="text-left font-medium px-4 py-2">Proposta</th>
                  <th className="text-right font-medium px-4 py-2">Valor</th>
                  <th className="text-left font-medium px-4 py-2">Pedidos</th>
                  <th className="text-left font-medium px-4 py-2">Links</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.demo_reconciliation_id} className="align-top">
                    <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                      {formatDateTimeBR(r.created_at)}
                      {r.created_by && <div className="text-xs text-slate-500">{r.created_by}</div>}
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-800">{r.customer_name}</td>
                    <td className="px-4 py-2 text-slate-700">{r.seller_name || '—'}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${
                          r.status === 'proposal_created'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        {demoStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{r.comercial_proposal_id ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-slate-800 whitespace-nowrap">
                      {formatCurrencyBRL(r.products_total)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.order_ids ?? []).map((id) => (
                          <Link key={id} to={`/sales-orders/${id}`} className="text-xs text-blue-700 hover:underline">
                            {id}
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 space-y-1">
                      {!r.payment_link && !r.pix_link ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <>
                          <CopyLink label="Pagamento" value={r.payment_link} compact />
                          <CopyLink label="PIX" value={r.pix_link} compact />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 text-sm text-slate-600">
            <span>
              Página {page} de {totalPages} · {total} conciliação(ões)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                title="Página anterior"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                title="Próxima página"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
